/**
 * Pure pivot engine for the HR Analytics report builder (no React).
 *
 * Takes the flat per-employee dataset from `/api/analytics/pivot-dataset` and a
 * pivot configuration, then groups + aggregates it entirely client-side. Also
 * flattens the result into `Record<string, any>[]` for the existing ExportButton
 * and reduces it to chart-ready data.
 */

import type { AggFn, FlatEmployeeRow } from "./fieldCatalog";

export type PivotMode = "pivot" | "flat";
export type FilterOp = "eq" | "neq" | "in" | "gte" | "lte";

export interface PivotValueSpec {
  field: keyof FlatEmployeeRow;
  agg: AggFn;
}

export interface PivotFilter {
  field: keyof FlatEmployeeRow;
  op: FilterOp;
  value: string | number | string[];
}

export interface PivotConfig {
  mode: PivotMode;
  /** Grouping dimensions for rows (>= 1 in pivot mode). */
  rowFields: (keyof FlatEmployeeRow)[];
  /** Optional second dimension for cross-tab columns. */
  colField?: keyof FlatEmployeeRow;
  /** Measures to aggregate. Empty => implicit Headcount (count of rows). */
  values: PivotValueSpec[];
  filters: PivotFilter[];
  /** Flat mode only: columns to project per employee. */
  flatColumns?: (keyof FlatEmployeeRow)[];
}

/** Internal cell key, e.g. "avg:base_salary" or "count:__headcount__". */
export type CellKey = string;
export const HEADCOUNT_KEY = "__headcount__";
export const TOTAL_COL = "__total__";

export interface PivotRow {
  /** One value per rowField, in order. */
  rowKey: string[];
  /** colValue -> { cellKey -> number|null }. Uses TOTAL_COL when no colField. */
  cells: Record<string, Record<CellKey, number | null>>;
}

export interface PivotResult {
  rowFields: (keyof FlatEmployeeRow)[];
  /** Distinct column values (sorted), or [TOTAL_COL] when no colField. */
  colValues: string[];
  /** The value specs actually computed (with a synthetic headcount spec if empty). */
  valueSpecs: PivotValueSpec[];
  rows: PivotRow[];
  grandTotals: Record<string, Record<CellKey, number | null>>;
}

export function cellKey(spec: PivotValueSpec): CellKey {
  return `${spec.agg}:${spec.field}`;
}

function displayValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

function passesFilter(row: FlatEmployeeRow, f: PivotFilter): boolean {
  const raw = row[f.field];
  switch (f.op) {
    case "eq":
      return displayValue(raw) === String(f.value);
    case "neq":
      return displayValue(raw) !== String(f.value);
    case "in":
      return Array.isArray(f.value) && f.value.includes(displayValue(raw));
    case "gte":
      return raw !== null && raw !== undefined && Number(raw) >= Number(f.value);
    case "lte":
      return raw !== null && raw !== undefined && Number(raw) <= Number(f.value);
    default:
      return true;
  }
}

function applyFilters(rows: FlatEmployeeRow[], filters: PivotFilter[]): FlatEmployeeRow[] {
  if (!filters.length) return rows;
  return rows.filter((r) => filters.every((f) => passesFilter(r, f)));
}

/** Aggregate a set of rows for a single value spec. */
function aggregate(rows: FlatEmployeeRow[], spec: PivotValueSpec): number | null {
  if (spec.field === (HEADCOUNT_KEY as keyof FlatEmployeeRow) || spec.agg === "count") {
    // count of rows that have a non-null value (or all rows for headcount)
    if (spec.field === (HEADCOUNT_KEY as keyof FlatEmployeeRow)) return rows.length;
    return rows.filter((r) => r[spec.field] !== null && r[spec.field] !== undefined).length;
  }
  const nums = rows
    .map((r) => r[spec.field])
    .filter((v): v is number => typeof v === "number" && !Number.isNaN(v));
  if (nums.length === 0) return null;
  switch (spec.agg) {
    case "sum":
      return nums.reduce((s, n) => s + n, 0);
    case "avg":
      return nums.reduce((s, n) => s + n, 0) / nums.length;
    case "min":
      return Math.min(...nums);
    case "max":
      return Math.max(...nums);
    default:
      return null;
  }
}

function aggregateAll(
  rows: FlatEmployeeRow[],
  specs: PivotValueSpec[]
): Record<CellKey, number | null> {
  const out: Record<CellKey, number | null> = {};
  for (const spec of specs) out[cellKey(spec)] = aggregate(rows, spec);
  return out;
}

/**
 * Compute a pivot (cross-tab) from the flat dataset.
 * In flat mode this returns an empty PivotResult — use `flatRows` for that path.
 */
export function computePivot(rows: FlatEmployeeRow[], config: PivotConfig): PivotResult {
  const filtered = applyFilters(rows, config.filters);

  // Synthesize a headcount spec when no measures were chosen.
  const valueSpecs: PivotValueSpec[] =
    config.values.length > 0
      ? config.values
      : [{ field: HEADCOUNT_KEY as keyof FlatEmployeeRow, agg: "count" }];

  // Distinct column values (cross-tab) or a single total column.
  let colValues: string[] = [TOTAL_COL];
  if (config.colField) {
    const set = new Set<string>();
    for (const r of filtered) set.add(displayValue(r[config.colField]));
    colValues = Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  // Group rows by composite rowKey.
  const groups = new Map<string, { rowKey: string[]; rows: FlatEmployeeRow[] }>();
  for (const r of filtered) {
    const rowKey = config.rowFields.map((f) => displayValue(r[f]));
    const k = rowKey.join(" ");
    let g = groups.get(k);
    if (!g) {
      g = { rowKey, rows: [] };
      groups.set(k, g);
    }
    g.rows.push(r);
  }

  const pivotRows: PivotRow[] = Array.from(groups.values())
    .sort((a, b) => a.rowKey.join(" ").localeCompare(b.rowKey.join(" ")))
    .map((g) => {
      const cells: Record<string, Record<CellKey, number | null>> = {};
      if (config.colField) {
        for (const col of colValues) {
          const sub = g.rows.filter((r) => displayValue(r[config.colField!]) === col);
          cells[col] = aggregateAll(sub, valueSpecs);
        }
      } else {
        cells[TOTAL_COL] = aggregateAll(g.rows, valueSpecs);
      }
      return { rowKey: g.rowKey, cells };
    });

  // Grand totals per column.
  const grandTotals: Record<string, Record<CellKey, number | null>> = {};
  if (config.colField) {
    for (const col of colValues) {
      const sub = filtered.filter((r) => displayValue(r[config.colField!]) === col);
      grandTotals[col] = aggregateAll(sub, valueSpecs);
    }
  } else {
    grandTotals[TOTAL_COL] = aggregateAll(filtered, valueSpecs);
  }

  return { rowFields: config.rowFields, colValues, valueSpecs, rows: pivotRows, grandTotals };
}

/** Flat (per-employee) projection of the filtered rows. */
export function flatRows(
  rows: FlatEmployeeRow[],
  config: PivotConfig,
  labelFor: (key: keyof FlatEmployeeRow) => string
): Record<string, any>[] {
  const filtered = applyFilters(rows, config.filters);
  const cols = config.flatColumns?.length ? config.flatColumns : (["name", "department", "status"] as (keyof FlatEmployeeRow)[]);
  return filtered.map((r) => {
    const o: Record<string, any> = {};
    for (const c of cols) o[labelFor(c)] = formatExportValue(r[c]);
    return o;
  });
}

function formatExportValue(v: unknown): string | number {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return Math.round(v * 100) / 100;
  return String(v);
}

function aggLabel(spec: PivotValueSpec, labelFor: (key: keyof FlatEmployeeRow) => string, headcountLabel: string): string {
  if (spec.field === (HEADCOUNT_KEY as keyof FlatEmployeeRow)) return headcountLabel;
  return `${spec.agg}(${labelFor(spec.field)})`;
}

/**
 * Flatten a pivot result into Record<string,any>[] for ExportButton (CSV/TSV).
 * Headers: row-field labels, then per column/value `agg(Field)` or `<col> · agg(Field)`.
 */
export function flattenPivotForExport(
  result: PivotResult,
  labelFor: (key: keyof FlatEmployeeRow) => string,
  opts: { headcountLabel: string; totalLabel: string }
): Record<string, any>[] {
  const hasCrossTab = !(result.colValues.length === 1 && result.colValues[0] === TOTAL_COL);

  const buildObj = (rowKey: string[] | null, cells: Record<string, Record<CellKey, number | null>>) => {
    const o: Record<string, any> = {};
    result.rowFields.forEach((f, i) => {
      o[labelFor(f)] = rowKey ? rowKey[i] : (i === 0 ? opts.totalLabel : "");
    });
    for (const col of result.colValues) {
      for (const spec of result.valueSpecs) {
        const base = aggLabel(spec, labelFor, opts.headcountLabel);
        const header = hasCrossTab ? `${col} · ${base}` : base;
        const v = cells[col]?.[cellKey(spec)];
        o[header] = v === null || v === undefined ? "" : Math.round(v * 100) / 100;
      }
    }
    return o;
  };

  const rows = result.rows.map((pr) => buildObj(pr.rowKey, pr.cells));
  rows.push(buildObj(null, result.grandTotals));
  return rows;
}

/** Reduce the pivot to {key,value}[] for a single-series chart (first value spec, total column). */
export function pivotToChartData(result: PivotResult): { key: string; value: number }[] {
  const spec = result.valueSpecs[0];
  if (!spec) return [];
  const ck = cellKey(spec);
  // Prefer the total column; fall back to summing across cross-tab columns.
  return result.rows.map((pr) => {
    let value = 0;
    if (pr.cells[TOTAL_COL]) {
      value = pr.cells[TOTAL_COL][ck] ?? 0;
    } else {
      for (const col of result.colValues) value += pr.cells[col]?.[ck] ?? 0;
    }
    return { key: pr.rowKey.join(" / ") || "—", value: Math.round(value * 100) / 100 };
  });
}
